import * as React from "react";
import { connect } from "react-redux";

import moment from "moment";

import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  withStyles,
} from "@material-ui/core";

import Calculating from "../../components/Loading/Calculating";
import CyComparison from "../../components/Charts/CyComparison";
import CyLineChart from "../../components/Charts/CyLineChart";
import CyPortfolioRiskMetrics from "../../components/Charts/CyPortfolioRiskMetrics";
import CyPortfolioDetailsSortable from "../../components/Charts/CyPortfolioDetailsSortable";
import CyPortfolioImplementation from "../../components/Charts/CyPortfolioImplementation";
import CyPortfolioOverview from "../../components/Charts/CyPortfolioOverview";
import DataBox from "../../components/UI/DataBox";
import PercentageNumber from "../../components/UI/PercentageNumber";
import SectionCaption from "../../components/UI/SectionCaption";
import SectionHeader from "../../components/UI/SectionHeader";
import {
  generateChartData,
  getRiskPeriod,
  getRiskPeriodLossTolerance,
} from "../../utilities/cpr";

import { appAction, portfolioAction } from "../../actions";

const styles = (theme) => ({
  overviewSection: {
    marginBottom: "3%",
  },
  overviewTable: {
    width: "100%",
  },
  summarySection: {
    margin: "40px 0",
  },
  subtitle: {
    fontWeight: 500,
    color: theme.palette.grey[800],
    margin: "2% 0 .8%",
  },
  tableCell: {
    padding: 2,
    fontSize: 12,
    "&.header": {
      maxWidth: 80,
      fontWeight: 500,
      fontSize: 13,
      wordWrap: "break-word",
      color: theme.palette.primary.main,
      // textAlign: 'center'
    },
    "&.wrap-cell-highlighted": {
      wordWrap: "break-word",
      maxWidth: 80,
      fontWeight: 500,
    },
    "&.wrap-cell-head": {
      wordWrap: "break-word",
      maxWidth: 140,
    },
  },
  scenarioCard: {
    width: "17% !important",
    [theme.breakpoints.down("md")]: {
      width: "26% !important",
    },
  },
});

// ClientPortfolio class - where displays the portfolio of client
class ClientPortfolio extends React.Component {
  state = {};

  componentDidMount() {
    const { match } = this.props;
    this.props.dispatch(
      portfolioAction.getPortfolio(
        match.params.portfolioId,
        match.params.householdId
      )
    );
    this.props.dispatch(appAction.toggleMainNav(false));
    this.props.dispatch(appAction.setAdminNav(true));
    window.scrollTo(0, 0);
  }

  render() {
    const { classes, portfolio, appName } = this.props;
    const currentPortfolio = portfolio && portfolio.currentPortfolio;

    if (!currentPortfolio) {
      return <Calculating />;
    }

    const cprRecommendation = currentPortfolio.cprRecommendation;
    const baseChart = generateChartData(portfolio.currentPortfolio);
    const annualWithdrawalChartData = !!currentPortfolio.metadata
      .annualWithdrawal
      ? generateChartData(
          currentPortfolio,
          currentPortfolio.metadata.annualWithdrawal
        )
      : null;
    const totalWithdrawal = !!currentPortfolio.metadata.annualWithdrawal
      ? generateChartData(
          currentPortfolio,
          currentPortfolio.metadata.annualWithdrawal,
          true
        )
      : null;

    const managerList = cprRecommendation && cprRecommendation.managers;
    const scenario =
      portfolio.currentPortfolio &&
      portfolio.currentPortfolio.metadata &&
      portfolio.currentPortfolio.metadata.scenario;
    const portfolioName =
      (portfolio.currentPortfolio && portfolio.currentPortfolio.name) || "";

    if (!baseChart) {
      return null;
    }

    const useWbiSingleStrategy =
      ['WBI Portfolios', 'WBI Envestnet', 'Other Models'].includes(portfolio.currentPortfolio.metadata.managerType);
    const strategyStartDate = moment(baseChart[0].date)
      .startOf("month")
      .subtract(2, "months");

    return (
      <React.Fragment>
        <SectionHeader
          text={`Summary ${portfolioName && `- ${portfolioName}`}`}
        />
        {scenario && (
          <DataBox
            fields={[["Scenario", scenario]]}
            className={classes.scenarioCard}
            horizontal
          />
        )}
        {cprRecommendation && (
          <div className={classes.overviewSection}>
            <Table className={classes.overviewTable}>
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell
                    align="right"
                    className={`${classes.tableCell} numeric header`}
                  >
                    Your Needs
                  </TableCell>
                  <TableCell
                    align="right"
                    className={`${classes.tableCell} numeric header`}
                  >
                    Your Selection
                  </TableCell>
                  <TableCell
                    align="right"
                    className={`${classes.tableCell} numeric header`}
                  >
                    {appName} Portfolio
                  </TableCell>
                  <TableCell
                    align="right"
                    className={`${classes.tableCell} numeric header`}
                  >
                    S&P 500
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell className={`${classes.tableCell} wrap-cell-head`}>
                    {getRiskPeriod(
                      portfolio.currentPortfolio.metadata.riskPeriod
                    )}{" "}
                    (Loss Tolerance)
                  </TableCell>
                  <TableCell
                    align="right"
                    className={`${classes.tableCell} wrap-cell-highlighted`}
                  >
                    <PercentageNumber
                      value={portfolio.currentPortfolio.metadata.risk}
                      showSign={true}
                    />
                  </TableCell>
                  <TableCell
                    align="right"
                    className={`${classes.tableCell} wrap-cell-highlighted`}
                  >
                    {portfolio.currentPortfolio.metadata.solveFor === "risk" ? (
                      <PercentageNumber
                        value={parseFloat(
                          portfolio.currentPortfolio.metadata.cprRisk
                        )}
                      />
                    ) : (
                      "--"
                    )}
                  </TableCell>
                  <TableCell
                    align="right"
                    className={`${classes.tableCell} wrap-cell-highlighted`}
                  >
                    <PercentageNumber
                      value={
                        cprRecommendation.summary.recommendationSummary[
                          getRiskPeriodLossTolerance(
                            portfolio.currentPortfolio.metadata.riskPeriod
                          )
                        ]
                      }
                      showSign={true}
                    />
                  </TableCell>
                  <TableCell
                    align="right"
                    className={`${classes.tableCell} wrap-cell-highlighted`}
                  >
                    <PercentageNumber
                      value={
                        cprRecommendation.summary.benchmark[
                          getRiskPeriodLossTolerance(
                            portfolio.currentPortfolio.metadata.riskPeriod
                          )
                        ]
                      }
                      showSign={true}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className={`${classes.tableCell} wrap-cell-head`}>
                    Annualized Rate of Return (RRoR)
                  </TableCell>
                  <TableCell
                    align="right"
                    className={`${classes.tableCell} wrap-cell-highlighted`}
                  >
                    <PercentageNumber
                      value={portfolio.currentPortfolio.metadata.rror}
                      showSign={true}
                    />
                  </TableCell>
                  <TableCell
                    align="right"
                    className={`${classes.tableCell} wrap-cell-highlighted`}
                  >
                    {portfolio.currentPortfolio.metadata.solveFor === "ret" ? (
                      <PercentageNumber
                        value={parseFloat(
                          portfolio.currentPortfolio.metadata.cprRror
                        )}
                      />
                    ) : (
                      "--"
                    )}
                  </TableCell>
                  <TableCell
                    align="right"
                    className={`${classes.tableCell} wrap-cell-highlighted`}
                  >
                    <PercentageNumber
                      value={
                        cprRecommendation.summary.recommendationSummary
                          .AnnualizedReturn
                      }
                      showSign={true}
                    />
                  </TableCell>
                  <TableCell
                    align="right"
                    className={`${classes.tableCell} wrap-cell-highlighted`}
                  >
                    <PercentageNumber
                      value={
                        cprRecommendation.summary.benchmark.AnnualizedReturn
                      }
                      showSign={true}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        )}
        {cprRecommendation && (
          <section className={classes.summarySection}>
            <SectionHeader text={`${appName} Portfolio`} />
            {managerList &&
              baseChart.length > 0 &&
              portfolio.currentPortfolio.metadata.riskPeriod && (
                <React.Fragment>
                  {useWbiSingleStrategy ? (
                    <Typography
                      variant="subtitle1"
                      className={classes.subtitle}
                    >
                      {cprRecommendation.displayName
                        ? `(${cprRecommendation.displayName})`
                        : ""}
                    </Typography>
                  ) : (
                    <Typography
                      variant="subtitle1"
                      className={classes.subtitle}
                    >
                      {appName} Portfolio:{" "}
                      {cprRecommendation.displayName &&
                        `(${cprRecommendation.displayName})`}
                    </Typography>
                  )}
                  <CyPortfolioDetailsSortable
                    strategyStartDate={strategyStartDate}
                    managerList={managerList}
                    portfolioSummary={cprRecommendation.summary}
                    finalWealth={baseChart.slice(-1)[0]}
                    finalWealthWithAnnualWithdrawal={
                      annualWithdrawalChartData
                        ? annualWithdrawalChartData.slice(-1)[0]
                        : null
                    }
                    riskPeriod={portfolio.currentPortfolio.metadata.riskPeriod}
                    totalWithdrawal={totalWithdrawal}
                  />
                  <Typography variant="subtitle1" className={classes.subtitle}>
                    {appName} Optimized Portfolio vs Benchmark
                  </Typography>
                  <CyPortfolioImplementation managerList={managerList} />
                </React.Fragment>
              )}
            {baseChart.length > 0 &&
              portfolio.currentPortfolio.metadata.riskPeriod &&
              portfolio.currentPortfolio.metadata.managerType !==
                "WBI Portfolios" && (
                <CyComparison
                  summaryData={cprRecommendation.summary}
                  finalWealth={baseChart.slice(-1)[0]}
                  finalWealthWithAnnualWithdrawal={
                    annualWithdrawalChartData
                      ? annualWithdrawalChartData.slice(-1)[0]
                      : null
                  }
                  riskPeriod={portfolio.currentPortfolio.metadata.riskPeriod}
                />
              )}
            <br />
            {baseChart.length > 0 && (
              <CyLineChart
                data={baseChart}
                initialInvestment={currentPortfolio.metadata.initialInvestment}
                portfolioName={currentPortfolio.name}
              />
            )}
          </section>
        )}
        {cprRecommendation && (
          <section className={`${classes.summarySection}`}>
            <SectionCaption>
              Let’s review some additional information about Cy’s proposed
              portfolio and manager strategies.
            </SectionCaption>
            <CyPortfolioRiskMetrics
              startDate={strategyStartDate.format("MM/DD/YYYY")}
              portfolioDisplayName={cprRecommendation.displayName || ""}
              managerList={managerList}
              portfolioSummary={cprRecommendation.summary}
            />
          </section>
        )}
        {portfolio.currentPortfolio && (
          <section className={`${classes.summarySection}`}>
            <SectionCaption>
              Let’s also review the inputs Cy used to estimate your{" "}
              <b>
                <PercentageNumber
                  value={portfolio.currentPortfolio.metadata.rror}
                  showSign={true}
                />
              </b>{" "}
              Required Rate of Return.
            </SectionCaption>
            <CyPortfolioOverview
              advisorConfig={portfolio.currentPortfolio.metadata.advisorConfig}
              clients={portfolio.currentPortfolio.Household.Clients}
            />
          </section>
        )}
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    portfolio: state.portfolio,
    app: state.app,
    appName: state.app.appName,
  };
};

export default withStyles(styles)(connect(mapStateToProps)(ClientPortfolio));



// WEBPACK FOOTER //
// ./src/containers/AdminClientPortfolio/index.jsx